/**
 * Oklahoma State-Specific Diagnostic Questions
 * CORRECTED VERSION - All citations verified against actual OAC 310:662
 * 
 * Regulatory Body: Oklahoma State Department of Health (OSDH)
 * Primary Regulation: OAC 310:662 - Home Care Agencies
 * Enabling Statute: 63 O.S. Section 1-1960 et seq. (Home Care Act)
 * 
 * CRITICAL CORRECTION: Original file cited OAC 310:663 throughout
 * OAC 310:663 = CONTINUUM OF CARE AND ASSISTED LIVING (nursing homes, ALFs)
 * OAC 310:662 = HOME CARE AGENCIES (correct regulation)
 * 
 * VERIFIED OAC 310:662 Structure:
 * - Subchapter 1: General Provisions (310:662-1-1, 310:662-1-2)
 * - Subchapter 2: Licenses (310:662-2-1)
 * - Subchapter 3: Administration (310:662-3-2 through 310:662-3-6)
 * - Subchapter 5: Client Services (310:662-5-1 through 310:662-5-4)
 * - Subchapter 6: Supportive Home Assistant Competency Testing
 * - Subchapter 7: Enforcement (310:662-7-1 through 310:662-7-7)
 * - Subchapter 8: Sitter or Companion Services
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const OKLAHOMA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'OK-A1',
    itemId: 'A1',
    stateCode: 'OK',
    title: 'OSDH Home Care Agency License Status',
    description: 'Oklahoma requires all home care agencies to be licensed under OAC 310:662',
    severity: 'high',
    regulationCode: '310:662-2-1',
    regulationName: 'Home Care Agency Licensure',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Agency license from OSDH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per OAC 310:662-2-1(g)(1), home care licenses expire July 31 each year.',
      },
      {
        id: 'q2',
        question: 'What is your home care agency license expiration date?',
        inputType: 'date',
        helpText: 'Oklahoma licenses expire July 31 annually per OAC 310:662-2-1(g)(1)',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your OSDH Home Care Agency license is current and active per OAC 310:662-2-1.',
      atRisk: 'Your license expires soon. Per OAC 310:662-2-1(h)(3), renewal applications must be filed at least 30 days before expiration.',
      nonCompliant: 'Operating without a valid OSDH license violates 63 O.S. Section 1-1960 et seq. Contact OSDH at (405) 426-8470.',
      unknown: 'Verify your home care agency license status with OSDH at (405) 426-8470.',
    },
    stateNotes: [
      'Licenses expire July 31 annually (OAC 310:662-2-1(g)(1))',
      'Initial license fee: $1,000; Renewal: $500 (OAC 310:662-2-1(d))',
      'License must be posted in conspicuous place (OAC 310:662-2-1(g)(4))',
      'Certified/accredited agencies exempt from routine inspections (OAC 310:662-7-1)',
    ],
  },

  {
    id: 'OK-A2',
    itemId: 'A2',
    stateCode: 'OK',
    title: 'Administrator Certification',
    description: 'Oklahoma requires certified home care administrator per OAC 310:662-3-4(c)',
    severity: 'high',
    regulationCode: '310:662-3-4(c) / OAC 310:664',
    regulationName: 'Administrator Certification Requirements',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service/home-care-administrator-registry.html',
    questions: [
      {
        id: 'q1',
        question: 'Is your administrator currently certified by OSDH as a Home Care Administrator?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per OAC 310:662-3-4(c), administrator must be properly certified by OSDH',
      },
      {
        id: 'q2',
        question: 'When does the administrator certification expire?',
        inputType: 'date',
        helpText: 'HCA certificates expire July 31 annually per OAC 310:664-11-1',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is proof of current certification posted in a conspicuous place per OAC 310:662-3-4(c)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'daysFuture', questionId: 'q2', value: 60 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator certification meets OAC 310:662-3-4(c) and OAC 310:664 requirements.',
      atRisk: 'Administrator certification may expire soon or posting requirement not met.',
      nonCompliant: 'Operating without a certified administrator violates OAC 310:662-3-4(c). Administrator must be OSDH certified.',
      unknown: 'Verify administrator certification with OSDH HCA Registry.',
    },
    stateNotes: [
      'HCA certification required per OAC 310:664',
      'Certificates expire July 31 annually',
      '12 hours continuing education required annually (OAC 310:664-9-2)',
      'Certification must be posted in conspicuous place',
    ],
  },

  {
    id: 'OK-A3',
    itemId: 'A3',
    stateCode: 'OK',
    title: 'Liability Insurance',
    description: 'Oklahoma requires liability insurance per OAC 310:662-2-1(c)(5)',
    severity: 'high',
    regulationCode: '310:662-2-1(c)(5)',
    regulationName: 'Insurance Requirements',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have liability insurance meeting Oklahoma minimum requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Minimum: $100,000 per occurrence, $300,000 aggregate per OAC 310:662-2-1(c)(5)',
      },
      {
        id: 'q2',
        question: 'What is your per occurrence coverage amount?',
        inputType: 'select',
        options: [
          { value: '100k_plus', label: '$100,000 or more' },
          { value: 'less_100k', label: 'Less than $100,000' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What is your aggregate coverage amount?',
        inputType: 'select',
        options: [
          { value: '300k_plus', label: '$300,000 or more' },
          { value: 'less_300k', label: 'Less than $300,000' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: '100k_plus' },
        { condition: 'equals', questionId: 'q3', value: '300k_plus' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'unsure' },
        { condition: 'equals', questionId: 'q3', value: 'unsure' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'less_100k' },
        { condition: 'equals', questionId: 'q3', value: 'less_300k' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Liability insurance meets OAC 310:662-2-1(c)(5) requirements ($100K/$300K).',
      atRisk: 'Unable to verify insurance coverage meets minimum requirements.',
      nonCompliant: 'Insurance does not meet Oklahoma minimum of $100,000/occurrence, $300,000 aggregate.',
      unknown: 'Verify insurance meets OAC 310:662-2-1(c)(5) requirements.',
    },
    stateNotes: [
      'Minimum $100,000 per occurrence required per OAC 310:662-2-1(c)(5)',
      'Minimum $300,000 aggregate coverage required per OAC 310:662-2-1(c)(5)',
      'Evidence of coverage must be submitted with license application',
      'Insurance lapse may result in license suspension',
    ],
  },

  {
    id: 'OK-A4',
    itemId: 'A4',
    stateCode: 'OK',
    title: 'Governing Body Requirements',
    description: 'Governing body requirements per OAC 310:662-3-4(a)(b)',
    severity: 'high',
    regulationCode: '310:662-3-4(a)(b)',
    regulationName: 'Governing Body Requirements',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an organized governing body legally responsible for conduct of the agency per OAC 310:662-3-4(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is agency ownership fully disclosed to OSDH per OAC 310:662-3-4(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
      {
        id: 'q3',
        question: 'Does your agency have an annual operating budget per OAC 310:662-3-4(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body meets OAC 310:662-3-4(a)(b) requirements.',
      atRisk: 'Some governing body requirements may not be fully met.',
      nonCompliant: 'No organized governing body in place. This violates OAC 310:662-3-4(a).',
      unknown: 'Review governing body structure against OAC 310:662-3-4.',
    },
    stateNotes: [
      'Governing body must have full legal authority per OAC 310:662-3-4(a)',
      'Ownership must be fully disclosed to OSDH per OAC 310:662-3-4(a)',
      'Annual operating budget required per OAC 310:662-3-4(b)',
      'Governing body appoints qualified administrator per OAC 310:662-3-4(c)',
    ],
  },

  {
    id: 'OK-A5',
    itemId: 'A5',
    stateCode: 'OK',
    title: 'Supervising Physician or Nurse',
    description: 'Supervision requirements per OAC 310:662-3-4(d)',
    severity: 'high',
    regulationCode: '310:662-3-4(d)',
    regulationName: 'Supervising Physician or Nurse Requirements',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'What type of services does your agency provide?',
        inputType: 'select',
        options: [
          { value: 'skilled', label: 'Skilled care (nursing, therapy)' },
          { value: 'personal', label: 'Personal care only' },
          { value: 'both', label: 'Both skilled and personal care' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For skilled care: Do you employ a physician or qualified supervising RN per OAC 310:662-3-4(d)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'includes',
          value: ['skilled', 'both'],
        },
      },
      {
        id: 'q3',
        question: 'For personal care only: Do you employ or contract with a supervising physician or RN per OAC 310:662-3-4(d)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'personal',
        },
      },
      {
        id: 'q4',
        question: 'Is a qualified RN alternate designated in writing per OAC 310:662-3-4(d)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['skilled', 'both'] },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'personal' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q4', value: 'yes', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervising physician/nurse requirements meet OAC 310:662-3-4(d).',
      atRisk: 'No written alternate RN designated. OAC 310:662-3-4(d) requires written designation.',
      nonCompliant: 'No supervising physician or RN in place. This violates OAC 310:662-3-4(d).',
      unknown: 'Review supervision requirements against OAC 310:662-3-4(d).',
    },
    stateNotes: [
      'Skilled care requires physician or qualified supervising RN per OAC 310:662-3-4(d)',
      'Personal care only may employ or contract supervising physician/RN per OAC 310:662-3-4(d)',
      'Written alternate RN designation required per OAC 310:662-3-4(d)',
      'Supervising RN responsible for clinical oversight of all nursing services',
    ],
  },

  {
    id: 'OK-A6',
    itemId: 'A6',
    stateCode: 'OK',
    title: 'Client Rights and Responsibilities',
    description: 'Client rights requirements per OAC 310:662-3-2',
    severity: 'high',
    regulationCode: '310:662-3-2',
    regulationName: 'Client Rights and Responsibilities',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written statement of client rights and responsibilities per OAC 310:662-3-2(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the statement provided verbally AND in writing before or at admission per OAC 310:662-3-2(a)?',
        inputType: 'select',
        options: [
          { value: 'both', label: 'Yes, both verbally and in writing' },
          { value: 'written_only', label: 'Written only' },
          { value: 'verbal_only', label: 'Verbal only' },
          { value: 'neither', label: 'Neither' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Which required elements are included? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services_charges', label: 'Available services, charges, billing (310:662-3-2(b)(1))' },
          { value: 'participate', label: 'Right to participate in care planning (310:662-3-2(b)(2))' },
          { value: 'advance_notice', label: 'Right to advance notice of care plan changes (310:662-3-2(b)(3))' },
          { value: 'client_responsibility', label: 'Client responsibility to treat staff with respect (310:662-3-2(b)(4))' },
          { value: 'confidentiality', label: 'Confidentiality explanation and consent requirement (310:662-3-2(b)(5))' },
          { value: 'property_respect', label: 'Right to have property and person treated with respect (310:662-3-2(b)(6))' },
          { value: 'grievance', label: 'Grievance procedure explanation (310:662-3-2(b)(7))' },
          { value: 'osdh_complaint', label: 'OSDH complaint information (1000 N.E. Tenth, OKC) (310:662-3-2(b)(8))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'neither', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'both' },
        { condition: 'countAtLeast', questionId: 'q3', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q2', value: ['written_only', 'verbal_only'] },
        { condition: 'countAtLeast', questionId: 'q3', value: 4 },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Client rights statement meets OAC 310:662-3-2 requirements.',
      atRisk: 'Client rights statement may be incomplete or not provided both verbally and in writing.',
      nonCompliant: 'No client rights statement in place. This violates OAC 310:662-3-2.',
      unknown: 'Review client rights procedures against OAC 310:662-3-2.',
    },
    stateNotes: [
      'Must be provided verbally AND in writing before or at admission per OAC 310:662-3-2(a)',
      'Include OSDH complaint address: 1000 N.E. Tenth, OKC per OAC 310:662-3-2(b)(8)',
      'All 8 required elements must be included per OAC 310:662-3-2(b)',
      'Signed acknowledgment should be maintained in client record',
    ],
  },

  {
    id: 'OK-A7',
    itemId: 'A7',
    stateCode: 'OK',
    title: 'Standards of Practice',
    description: 'Standards of practice requirements per OAC 310:662-3-3',
    severity: 'high',
    regulationCode: '310:662-3-3',
    regulationName: 'Standards of Practice',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Has your governing body adopted written standards of practice per OAC 310:662-3-3(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required items are included in your standards of practice? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'no_mislead', label: 'No misleading clients about services/charges (310:662-3-3(b)(1))' },
          { value: 'no_misappropriate', label: 'No misuse/misappropriation of client property (310:662-3-3(b)(2))' },
          { value: 'no_recruit', label: 'No recruiting clients from other agencies (310:662-3-3(b)(3))' },
          { value: 'professional_referral', label: 'Professional referral acceptance, no remuneration (310:662-3-3(b)(4))' },
          { value: 'no_solicitation', label: 'No solicitation by coercion/harassment (310:662-3-3(b)(5))' },
          { value: 'guardian_restriction', label: 'Guardian restrictions (310:662-3-3(b)(6))' },
          { value: 'accepted_standards', label: 'Services per accepted standards of practice (310:662-3-3(b)(7))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Standards of practice meet OAC 310:662-3-3 requirements.',
      atRisk: 'Standards of practice may be missing required elements.',
      nonCompliant: 'No written standards of practice. This violates OAC 310:662-3-3.',
      unknown: 'Review standards of practice against OAC 310:662-3-3.',
    },
    stateNotes: [
      'Adopted by governing body per OAC 310:662-3-3(a)',
      'Must prohibit misleading clients, misappropriation, and coerced solicitation',
      'All 7 elements per OAC 310:662-3-3(b) required',
      'Services must be per accepted professional standards per OAC 310:662-3-3(b)(7)',
    ],
  },

  {
    id: 'OK-A8',
    itemId: 'A8',
    stateCode: 'OK',
    title: 'Client Care Policies',
    description: 'Client care policy requirements per OAC 310:662-3-6',
    severity: 'high',
    regulationCode: '310:662-3-6',
    regulationName: 'Client Care Policies',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written client care policies per OAC 310:662-3-6?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required policy areas are addressed? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'infection', label: 'Infection control in home care setting (310:662-3-6(b)(1))' },
          { value: 'safety', label: 'Safety assessment and injury prevention (310:662-3-6(b)(2))' },
          { value: 'emergency', label: 'Emergency medical situations management (310:662-3-6(b)(3))' },
          { value: 'autonomy', label: 'Efforts to maximize client autonomy (310:662-3-6(b)(4))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client care policies meet OAC 310:662-3-6 requirements.',
      atRisk: 'Client care policies may be missing required areas.',
      nonCompliant: 'No written client care policies. This violates OAC 310:662-3-6.',
      unknown: 'Review client care policies against OAC 310:662-3-6.',
    },
    stateNotes: [
      'Infection control in home care setting required per OAC 310:662-3-6(b)(1)',
      'Safety assessment and injury prevention required per OAC 310:662-3-6(b)(2)',
      'Emergency medical situations management required per OAC 310:662-3-6(b)(3)',
      'Maximize client autonomy policies required per OAC 310:662-3-6(b)(4)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL
  // ============================================
  {
    id: 'OK-B1',
    itemId: 'B1',
    stateCode: 'OK',
    title: 'Personnel Policies',
    description: 'Personnel policy requirements per OAC 310:662-3-4(e)',
    severity: 'high',
    regulationCode: '310:662-3-4(e)',
    regulationName: 'Personnel Policies',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Which required personnel policies do you have? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'employment', label: 'Employment procedures (310:662-3-4(e)(1))' },
          { value: 'orientation', label: 'Orientation and in-service programs (310:662-3-4(e)(2))' },
          { value: 'job_desc', label: 'Job descriptions and qualifications (310:662-3-4(e)(3))' },
          { value: 'evaluations', label: 'Periodic performance evaluations (310:662-3-4(e)(4))' },
          { value: 'disciplinary', label: 'Disciplinary action procedures (310:662-3-4(e)(5))' },
          { value: 'health_screening', label: 'Health screening including TB testing (310:662-3-4(e)(6))' },
          { value: 'influenza', label: 'Annual influenza vaccination program (310:662-3-4(e)(7))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel policies meet OAC 310:662-3-4(e) requirements.',
      atRisk: 'Personnel policies are missing some required elements.',
      nonCompliant: 'Personnel policies are significantly incomplete per OAC 310:662-3-4(e).',
      unknown: 'Review personnel policies against OAC 310:662-3-4(e).',
    },
    stateNotes: [
      'All 7 policy areas required per OAC 310:662-3-4(e)',
      'Must include employment, orientation, job descriptions, evaluations, disciplinary action',
      'Health screening including TB testing required per OAC 310:662-3-4(e)(6)',
      'Annual influenza vaccination program required per OAC 310:662-3-4(e)(7)',
    ],
  },

  {
    id: 'OK-B2',
    itemId: 'B2',
    stateCode: 'OK',
    title: 'Personnel Records',
    description: 'Personnel record requirements per OAC 310:662-3-4(f)',
    severity: 'high',
    regulationCode: '310:662-3-4(f)',
    regulationName: 'Personnel Records',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Which required items do you maintain in personnel records? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'qualifications', label: 'Qualifications' },
          { value: 'employment_history', label: 'Employment history' },
          { value: 'orientation', label: 'Records of orientation' },
          { value: 'inservice', label: 'Records of in-service provided' },
          { value: 'health_screening', label: 'Health screening verification' },
          { value: 'evaluations', label: 'Performance evaluations' },
          { value: 'disciplinary', label: 'Record of disciplinary actions' },
          { value: 'licensure', label: 'Current licensure/certification verification' },
        ],
        required: true,
        helpText: 'OAC 310:662-3-4(f) specifies required personnel record contents',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel records contain all required elements per OAC 310:662-3-4(f).',
      atRisk: 'Personnel records may be missing some required elements.',
      nonCompliant: 'Personnel records are incomplete per OAC 310:662-3-4(f).',
      unknown: 'Review personnel records against OAC 310:662-3-4(f).',
    },
    stateNotes: [
      'All 8 elements required per OAC 310:662-3-4(f)',
      'Must include qualifications, employment history, orientation, in-service',
      'Health screening, evaluations, disciplinary actions, and licensure verification required',
      'Records must be maintained on file and available for OSDH inspection',
    ],
  },

  {
    id: 'OK-B2a',
    itemId: 'B2a',
    stateCode: 'OK',
    title: 'Criminal Background Checks',
    description: 'Background check requirements for home care agency personnel',
    severity: 'high',
    regulationCode: '310:662-3-4(e) / 63 O.S. Section 1-1960',
    regulationName: 'Personnel Background Screening Requirements',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct criminal background checks for all staff with client contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff before client contact' },
          { value: 'most', label: 'Most staff, some pending' },
          { value: 'no', label: 'No / Not consistently' },
        ],
        required: true,
        helpText: 'Per OAC 310:662-3-4(e): Background screening is required for personnel with client contact.',
      },
      {
        id: 'q2',
        question: 'Do employees begin working with clients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per OAC 310:662-3-4(e): Employees must not have client contact until background check clearance is received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - must be evaluated FIRST)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if not obtaining background checks
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT if all checks complete AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet Oklahoma requirements.',
      atRisk: 'Some background checks may be incomplete or pending.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per OAC 310:662-3-4(e), employees must not have client contact until background check clearance is received. Immediately remove uncleared staff from client care.',
      unknown: 'Verify background check procedures comply with Oklahoma law.',
    },
    stateNotes: [
      'Background checks required for all client contact personnel',
      'No client contact allowed until clearance received',
      'Contact OSDH at (405) 426-8470 for guidance',
    ],
  },

  {
    id: 'OK-B3',
    itemId: 'B3',
    stateCode: 'OK',
    title: 'TB Testing Requirements',
    description: 'Tuberculosis screening requirements per OAC 310:662-3-4(e)(6)',
    severity: 'high',
    regulationCode: '310:662-3-4(e)(6)',
    regulationName: 'TB Screening',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff with direct client contact have initial and annual TB skin tests per OAC 310:662-3-4(e)(6)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB clearance' },
          { value: 'most', label: 'Most staff, some may be expired' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For staff with positive TB history, do you have documented negative chest x-ray and symptom assessment per OAC 310:662-3-4(e)(6)?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, properly documented' },
          { value: 'some', label: 'Some documented' },
          { value: 'no', label: 'No / Not applicable' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening meets OAC 310:662-3-4(e)(6) requirements (initial and annual).',
      atRisk: 'Some staff may have expired TB clearance. Review all files.',
      nonCompliant: 'TB screening not being tracked. This violates OAC 310:662-3-4(e)(6).',
      unknown: 'Review TB screening procedures against OAC 310:662-3-4(e)(6).',
    },
    stateNotes: [
      'Initial and annual TB skin tests required',
      'Positive history: documented negative chest x-ray and no symptoms exempts from testing',
    ],
  },

  {
    id: 'OK-B4',
    itemId: 'B4',
    stateCode: 'OK',
    title: 'Influenza Vaccination Program',
    description: 'Annual influenza vaccination requirements per OAC 310:662-3-4(e)(7)',
    severity: 'medium',
    regulationCode: '310:662-3-4(e)(7)',
    regulationName: 'Influenza Vaccination Program',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an annual influenza vaccination program per OAC 310:662-3-4(e)(7)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required elements does your program include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'onsite', label: 'Offer vaccination onsite at no charge (310:662-3-4(e)(7)(A))' },
          { value: 'documentation', label: 'Documentation of vaccination or signed declination (310:662-3-4(e)(7)(B))' },
          { value: 'education', label: 'Education on flu, transmission, control measures (310:662-3-4(e)(7)(C))' },
          { value: 'annual_eval', label: 'Annual evaluation of program including non-participation reasons (310:662-3-4(e)(7)(D))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Influenza vaccination program meets OAC 310:662-3-4(e)(7) requirements.',
      atRisk: 'Influenza vaccination program may be missing required elements.',
      nonCompliant: 'No influenza vaccination program. This violates OAC 310:662-3-4(e)(7).',
      unknown: 'Review influenza program against OAC 310:662-3-4(e)(7).',
    },
    stateNotes: [
      'Vaccination offered onsite at no charge per OAC 310:662-3-4(e)(7)(A)',
      'Documentation of vaccination or signed declination required per OAC 310:662-3-4(e)(7)(B)',
      'Education on flu, transmission, control measures required per OAC 310:662-3-4(e)(7)(C)',
      'Annual program evaluation including non-participation reasons per OAC 310:662-3-4(e)(7)(D)',
    ],
  },

  {
    id: 'OK-B5',
    itemId: 'B5',
    stateCode: 'OK',
    title: 'Home Health Aide Certification',
    description: 'HHA certification requirements per OAC 310:662-5-3(f)',
    severity: 'high',
    regulationCode: '310:662-5-3(f)',
    regulationName: 'Home Health Aide Certification',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL home health aides certified by OSDH and on the Home Health Aide Registry per OAC 310:662-5-3(f)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all are certified and on registry' },
          { value: 'most', label: 'Most are certified' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'All unlicensed personal care providers must be certified regardless of job title',
      },
      {
        id: 'q2',
        question: 'Are any aides working more than 4 months without certification per OAC 310:662-5-3(f)?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - all certified or within 4 month window' },
          { value: 'yes', label: 'Yes - some exceed 4 months uncertified' },
        ],
        helpText: 'Maximum 4 months employment without certification',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'All HHAs are certified and on OSDH registry per OAC 310:662-5-3(f).',
      atRisk: 'Some HHAs may not be certified. Verify all are on OSDH registry.',
      nonCompliant: 'HHAs working without certification or exceeding 4-month limit. This violates OAC 310:662-5-3(f).',
      unknown: 'Verify all HHAs are certified and on OSDH registry.',
    },
    stateNotes: [
      'All unlicensed personal care providers must be certified regardless of job title',
      'Maximum 4 months employment without certification',
      'Must be on OSDH Home Health Aide Registry',
    ],
  },

  // ============================================
  // SECTION C: CLINICAL RECORDS
  // ============================================
  {
    id: 'OK-C1',
    itemId: 'C1',
    stateCode: 'OK',
    title: 'Clinical Record Requirements',
    description: 'Clinical record requirements per OAC 310:662-3-5',
    severity: 'high',
    regulationCode: '310:662-3-5',
    regulationName: 'Clinical Records',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain a clinical record for each client per OAC 310:662-3-5(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required elements do clinical records contain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'identifying', label: 'Identifying info, household members, emergency contacts (310:662-3-5(d)(1))' },
          { value: 'assessment', label: 'Initial assessment including health history (310:662-3-5(d)(2))' },
          { value: 'limitations', label: 'Functional limitations and activity restrictions (310:662-3-5(d)(3))' },
          { value: 'condition_change', label: 'Documentation of condition changes (310:662-3-5(d)(4))' },
          { value: 'visit_notes', label: 'Notes for each service with date, service, name, title, signature (310:662-3-5(d)(5))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per OAC 310:662-3-5(d).',
      atRisk: 'Clinical records may be missing some required elements.',
      nonCompliant: 'Clinical records are incomplete or not maintained per OAC 310:662-3-5.',
      unknown: 'Review clinical records against OAC 310:662-3-5.',
    },
    stateNotes: [
      'All 5 required elements per OAC 310:662-3-5(d) must be present',
      'Includes identifying info, assessment, limitations, condition changes, visit notes',
      'Notes must include date, service, name, title, and signature per OAC 310:662-3-5(d)(5)',
      'Records must be kept confidential per OAC 310:662-3-5(a)',
    ],
  },

  {
    id: 'OK-C2',
    itemId: 'C2',
    stateCode: 'OK',
    title: 'Skilled Care Clinical Records',
    description: 'Additional clinical record requirements for skilled care per OAC 310:662-3-5(e)',
    severity: 'high',
    regulationCode: '310:662-3-5(e)',
    regulationName: 'Skilled Care Clinical Records',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide skilled care or personal care by physician order?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - personal care only, no physician orders' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For skilled care, which additional elements do clinical records contain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physician_name', label: 'Physician name and telephone number (310:662-3-5(e)(1))' },
          { value: 'clinical_notes', label: 'Signed/dated clinical notes documenting services and response (310:662-3-5(e)(2))' },
          { value: 'physician_orders', label: 'Physician orders sent within 10 days for signature (310:662-3-5(e)(3))' },
          { value: 'discharge_summary', label: 'Discharge summary with copy to physician (310:662-3-5(e)(4))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Skilled care clinical records meet OAC 310:662-3-5(e) requirements.',
      atRisk: 'Skilled care records may be missing required elements.',
      nonCompliant: 'Skilled care records do not meet OAC 310:662-3-5(e) requirements.',
      unknown: 'Review skilled care records against OAC 310:662-3-5(e).',
    },
    stateNotes: [
      'Physician name and telephone required per OAC 310:662-3-5(e)(1)',
      'Signed/dated clinical notes documenting services and response per OAC 310:662-3-5(e)(2)',
      'Physician orders sent within 10 days for signature per OAC 310:662-3-5(e)(3)',
      'Discharge summary with copy to physician required per OAC 310:662-3-5(e)(4)',
    ],
  },

  {
    id: 'OK-C3',
    itemId: 'C3',
    stateCode: 'OK',
    title: 'Record Retention (5 Years)',
    description: 'Record retention requirements per OAC 310:662-3-5(c)',
    severity: 'high',
    regulationCode: '310:662-3-5(c)',
    regulationName: 'Record Retention',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records after the client was last seen?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to5', label: '3-5 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'OAC 310:662-3-5(c) requires minimum 5 years retention',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to5', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets 5-year minimum per OAC 310:662-3-5(c).',
      atRisk: 'Record retention may not meet 5-year requirement.',
      nonCompliant: 'Record retention does not meet 5-year requirement per OAC 310:662-3-5(c).',
      unknown: 'Review record retention policy against OAC 310:662-3-5(c).',
    },
    stateNotes: [
      'Minimum 5 years retention after client last seen per OAC 310:662-3-5(c)',
      'Applies to both paper and electronic records',
      'Records must remain accessible for OSDH inspection during retention period',
      'Consider longer retention for minors and potential litigation',
    ],
  },

  {
    id: 'OK-C4',
    itemId: 'C4',
    stateCode: 'OK',
    title: 'Record Confidentiality',
    description: 'Record confidentiality requirements per OAC 310:662-3-5(a)(b)',
    severity: 'high',
    regulationCode: '310:662-3-5(a)(b)',
    regulationName: 'Record Confidentiality',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Are clinical records kept confidential with written policies per OAC 310:662-3-5(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are records stored in a locked area with only authorized personnel access per OAC 310:662-3-5(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Are records released only upon written consent, court order, or as authorized by law per OAC 310:662-3-5(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Record confidentiality meets OAC 310:662-3-5(a)(b) requirements.',
      atRisk: 'Record security or release procedures may need improvement.',
      nonCompliant: 'No written confidentiality policies. This violates OAC 310:662-3-5(a).',
      unknown: 'Review confidentiality procedures against OAC 310:662-3-5(a)(b).',
    },
    stateNotes: [
      'Written confidentiality policies required per OAC 310:662-3-5(a)',
      'Records stored in locked area with authorized personnel access only',
      'Release only upon written consent, court order, or as authorized by law per OAC 310:662-3-5(b)',
      'HIPAA compliance also required for protected health information',
    ],
  },

  // ============================================
  // SECTION D: CLIENT SERVICES
  // ============================================
  {
    id: 'OK-D1',
    itemId: 'D1',
    stateCode: 'OK',
    title: 'Initial Assessment',
    description: 'Initial assessment requirements per OAC 310:662-5-1',
    severity: 'high',
    regulationCode: '310:662-5-1',
    regulationName: 'Initiation of Services',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Is an initial assessment performed in the client\'s residence per OAC 310:662-5-1(a)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before or at service initiation' },
          { value: 'usually', label: 'Usually, but sometimes after' },
          { value: 'no', label: 'No formal assessment process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Who performs the initial assessment?',
        inputType: 'select',
        options: [
          { value: 'qualified', label: 'Physician, RN, or qualified therapist as appropriate' },
          { value: 'other', label: 'Other staff' },
          { value: 'none', label: 'No formal assessment' },
        ],
        helpText: 'OAC 310:662-5-1(a) requires physician, RN, or qualified therapist',
      },
      {
        id: 'q3',
        question: 'For skilled care, do you have a physician order BEFORE the in-home assessment per OAC 310:662-5-1(c)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before assessment' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / Not applicable' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'other', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'qualified' },
        { condition: 'equals', questionId: 'q3', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      // No physician order before skilled care assessment per OAC 310:662-5-1(c)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Initial assessment meets OAC 310:662-5-1 requirements.',
      atRisk: 'Initial assessment may not be consistently performed before services or physician orders for skilled care may be inconsistent per OAC 310:662-5-1(c).',
      nonCompliant: 'No initial assessment process or skilled care provided without physician order. This violates OAC 310:662-5-1.',
      unknown: 'Review initial assessment procedures against OAC 310:662-5-1.',
    },
    stateNotes: [
      'In-home assessment required per OAC 310:662-5-1(a)',
      'Must be performed by physician, RN, or qualified therapist per OAC 310:662-5-1(a)',
      'For skilled care: physician order required BEFORE assessment per OAC 310:662-5-1(c)',
      'Assessment determines appropriateness of services and develops care plan',
    ],
  },

  {
    id: 'OK-D2',
    itemId: 'D2',
    stateCode: 'OK',
    title: 'Plan of Care - Personal Care (6 Month Review)',
    description: 'Personal care plan requirements per OAC 310:662-5-2(a)',
    severity: 'high',
    regulationCode: '310:662-5-2(a)',
    regulationName: 'Plan of Care - Non-Skilled',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide personal care (non-skilled) services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the plan of care prepared by a physician or RN at initial assessment per OAC 310:662-5-2(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is the plan of care reviewed and updated by RN at least every 6 months per OAC 310:662-5-2(a)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 6 months or more frequently' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No regular review schedule' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personal care plans meet OAC 310:662-5-2(a) requirements (6-month review).',
      atRisk: 'Personal care plan reviews may not occur every 6 months.',
      nonCompliant: 'Personal care plans do not meet OAC 310:662-5-2(a) requirements.',
      unknown: 'Review personal care plan procedures against OAC 310:662-5-2(a).',
    },
    stateNotes: [
      'Plan prepared by physician or RN at initial assessment per OAC 310:662-5-2(a)',
      'Review and update by RN at least every 6 months per OAC 310:662-5-2(a)',
      'Review sooner if client condition changes significantly',
      'Client/family should participate in care planning process',
    ],
  },

  {
    id: 'OK-D3',
    itemId: 'D3',
    stateCode: 'OK',
    title: 'Plan of Care - Skilled Care (62 Day Review)',
    description: 'Skilled care plan requirements per OAC 310:662-5-2(b)',
    severity: 'high',
    regulationCode: '310:662-5-2(b)',
    regulationName: 'Plan of Care - Skilled',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide skilled care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is there a continuing review of clinical records every 62 days per OAC 310:662-5-2(b)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 62 days' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No regular review schedule' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do skilled care plans include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnoses', label: 'All pertinent diagnoses including mental status' },
          { value: 'services_equipment', label: 'Types of services and equipment required' },
          { value: 'frequency', label: 'Frequency of visits/hours' },
          { value: 'prognosis', label: 'Prognosis' },
          { value: 'limitations', label: 'Functional limitations' },
          { value: 'activities', label: 'Activities permitted' },
          { value: 'nutrition', label: 'Nutritional requirements' },
          { value: 'medications', label: 'Medications and treatments' },
          { value: 'safety', label: 'Safety measures' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q3', value: 8 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Skilled care plans meet OAC 310:662-5-2(b) requirements (62-day review).',
      atRisk: 'Skilled care plan reviews may not occur every 62 days.',
      nonCompliant: 'Skilled care plans do not meet OAC 310:662-5-2(b) requirements.',
      unknown: 'Review skilled care plan procedures against OAC 310:662-5-2(b).',
    },
    stateNotes: [
      'Continuing review of clinical records every 62 days per OAC 310:662-5-2(b)',
      'Must include all 9 required elements (diagnoses, services, frequency, prognosis, etc.)',
      'Mental status assessment required',
      'Medications, treatments, nutritional requirements, safety measures documented',
    ],
  },

  {
    id: 'OK-D4',
    itemId: 'D4',
    stateCode: 'OK',
    title: 'Supervision of HHA Services - Skilled (60 Days)',
    description: 'Supervision requirements for HHA with skilled care per OAC 310:662-5-3(h)(1)',
    severity: 'high',
    regulationCode: '310:662-5-3(h)(1)',
    regulationName: 'Supervision of Services - Skilled',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide HHA/personal care services in conjunction with skilled services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does an RN make a supervisory visit to the client home at least every 60 days per OAC 310:662-5-3(h)(1)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 60 days or more frequently' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No / Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision of HHA with skilled care meets OAC 310:662-5-3(h)(1) (every 60 days).',
      atRisk: 'RN supervisory visits may not occur every 60 days.',
      nonCompliant: 'RN supervision does not meet 60-day requirement per OAC 310:662-5-3(h)(1).',
      unknown: 'Review RN supervision procedures against OAC 310:662-5-3(h)(1).',
    },
    stateNotes: [
      'RN supervisory visit at least every 60 days per OAC 310:662-5-3(h)(1)',
      'Visit made to client home with HHA services in conjunction with skilled care',
      'More frequent supervision if client condition warrants',
      'Document supervisory visit in client record',
    ],
  },

  {
    id: 'OK-D5',
    itemId: 'D5',
    stateCode: 'OK',
    title: 'Supervision of HHA Services - Personal Care Only (6 Months)',
    description: 'Supervision requirements for HHA without skilled care per OAC 310:662-5-3(h)(3)',
    severity: 'high',
    regulationCode: '310:662-5-3(h)(3)',
    regulationName: 'Supervision of Services - Personal Care Only',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ONLY HHA/personal care services (no skilled care)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - we also provide skilled care' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does a physician or licensed nurse make a supervisory visit at least every 6 months per OAC 310:662-5-3(h)(3)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 6 months or more frequently' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No / Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervision of personal care only meets OAC 310:662-5-3(h)(3) (every 6 months).',
      atRisk: 'Supervisory visits may not occur every 6 months.',
      nonCompliant: 'Supervision does not meet 6-month requirement per OAC 310:662-5-3(h)(3).',
      unknown: 'Review supervision procedures against OAC 310:662-5-3(h)(3).',
    },
    stateNotes: [
      'Physician or licensed nurse supervisory visit at least every 6 months per OAC 310:662-5-3(h)(3)',
      'Applies to clients receiving ONLY HHA/personal care services (no skilled care)',
      'Assess client needs and HHA performance during visit',
      'Document supervisory visit in client record',
    ],
  },

  // ============================================
  // SECTION E: QUALITY & COMPLIANCE
  // ============================================
  {
    id: 'OK-E1',
    itemId: 'E1',
    stateCode: 'OK',
    title: 'Quality Assessment and Improvement',
    description: 'QA/QI program requirements per OAC 310:662-5-4',
    severity: 'high',
    regulationCode: '310:662-5-4',
    regulationName: 'Quality Assessment and Improvement',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an ongoing QA/QI program approved by the governing body per OAC 310:662-5-4(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the program include these required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'methods', label: 'Methods to assess all services including contracted (310:662-5-4(b)(1))' },
          { value: 'satisfaction', label: 'Client satisfaction surveys (310:662-5-4(b)(1))' },
          { value: 'record_reviews', label: 'Sample clinical record reviews (310:662-5-4(b)(1))' },
          { value: 'frequency', label: 'Defined frequency and responsible staff (310:662-5-4(b)(2))' },
          { value: 'quarterly_reports', label: 'Written reports to administrator and governing body at least quarterly (310:662-5-4(b)(2))' },
          { value: 'reassessment', label: 'Methods to reassess improvements (310:662-5-4(b)(2))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QA/QI program meets OAC 310:662-5-4 requirements.',
      atRisk: 'QA/QI program may be missing required elements.',
      nonCompliant: 'No QA/QI program in place. This violates OAC 310:662-5-4.',
      unknown: 'Review QA/QI program against OAC 310:662-5-4.',
    },
    stateNotes: [
      'Ongoing QA/QI program approved by governing body per OAC 310:662-5-4(a)',
      'Methods to assess all services including contracted per OAC 310:662-5-4(b)(1)',
      'Written reports to administrator and governing body quarterly per OAC 310:662-5-4(b)(2)',
      'Client satisfaction surveys and sample clinical record reviews required',
    ],
  },

  {
    id: 'OK-E2',
    itemId: 'E2',
    stateCode: 'OK',
    title: 'Contracted Services',
    description: 'Contract service requirements per OAC 310:662-3-4(g)',
    severity: 'medium',
    regulationCode: '310:662-3-4(g)',
    regulationName: 'Contracted Services',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you contract with other entities to provide home care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do written agreements include required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Services to be provided (310:662-3-4(g)(1)(A))' },
          { value: 'coordination', label: 'How services will be coordinated, evaluated, supervised (310:662-3-4(g)(1)(B))' },
          { value: 'care_plan', label: 'Process for care plan development/revision (310:662-3-4(g)(1)(C))' },
          { value: 'scheduling', label: 'Process for scheduling visits (310:662-3-4(g)(1)(D))' },
          { value: 'documentation', label: 'Procedures for submitting notes to clinical record (310:662-3-4(g)(1)(E))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do you verify contractor licensure/certification per OAC 310:662-3-4(g)(2)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracted services meet OAC 310:662-3-4(g) requirements.',
      atRisk: 'Contract agreements may be missing required elements.',
      nonCompliant: 'Contract arrangements do not meet OAC 310:662-3-4(g) requirements.',
      unknown: 'Review contract arrangements against OAC 310:662-3-4(g).',
    },
    stateNotes: [
      'Written agreements must include all 5 elements per OAC 310:662-3-4(g)(1)',
      'Verify contractor licensure/certification per OAC 310:662-3-4(g)(2)',
      'Services, coordination, care plan process, scheduling, and documentation procedures required',
      'Agency remains responsible for quality of contracted services',
    ],
  },

  {
    id: 'OK-E3',
    itemId: 'E3',
    stateCode: 'OK',
    title: 'Infectious Waste Disposal',
    description: 'Infectious waste requirements per OAC 310:662-5-3.1',
    severity: 'medium',
    regulationCode: '310:662-5-3.1',
    regulationName: 'Infectious Waste Disposal',
    agencyName: 'Oklahoma State Department of Health',
    agencyAbbr: 'OSDH',
    agencyPhone: '(405) 426-8470',
    agencyUrl: 'https://oklahoma.gov/health/services/licensing-inspections/health-resources-development-service.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency generate infectious wastes in providing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are sharps packaged in rigid, leak-proof, puncture-resistant containers per OAC 310:662-5-3.1(a)(1)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do you assess clients at admission for infectious waste/sharps generation and provide education per OAC 310:662-5-3.1(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infectious waste disposal meets OAC 310:662-5-3.1 requirements.',
      atRisk: 'Infectious waste procedures may need review.',
      nonCompliant: 'Infectious waste disposal does not meet OAC 310:662-5-3.1 requirements.',
      unknown: 'Review infectious waste procedures against OAC 310:662-5-3.1.',
    },
    stateNotes: [
      'Sharps in rigid, leak-proof, puncture-resistant containers per OAC 310:662-5-3.1(a)(1)',
      'Assess clients at admission for infectious waste/sharps generation per OAC 310:662-5-3.1(b)',
      'Provide client education on proper disposal',
      'Follow OSHA and EPA guidelines for infectious waste',
    ],
  },

  // ============================================
  // SECTION F: MEDICAID/MEDICARE
  // ============================================
  {
    id: 'OK-F1',
    itemId: 'F1',
    stateCode: 'OK',
    title: 'SoonerCare (Medicaid) Provider Enrollment',
    description: 'Oklahoma Health Care Authority enrollment for Medicaid providers',
    severity: 'high',
    regulationCode: '317:30-5-125',
    regulationName: 'SoonerCare Provider Enrollment',
    agencyName: 'Oklahoma Health Care Authority',
    agencyAbbr: 'OHCA',
    agencyPhone: '(405) 522-7300',
    agencyUrl: 'https://oklahoma.gov/ohca/providers.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide SoonerCare (Medicaid) funded services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as an OHCA SoonerCare provider?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes, currently enrolled' },
          { value: 'pending', label: 'Enrollment pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'SoonerCare enrollment is current or not required.',
      atRisk: 'SoonerCare enrollment is pending. Monitor status with OHCA.',
      nonCompliant: 'Providing SoonerCare services without enrollment. Contact OHCA at (405) 522-7300.',
      unknown: 'Verify SoonerCare enrollment if providing Medicaid services.',
    },
    stateNotes: [
      'SoonerCare is Oklahoma\'s Medicaid program administered by OHCA',
      'Enrollment required before billing for Medicaid services per 317:30-5-125',
      'Contact OHCA Provider Enrollment at (405) 522-7300',
      'Maintain active enrollment status and comply with all OHCA requirements',
    ],
  },

  {
    id: 'OK-F2',
    itemId: 'F2',
    stateCode: 'OK',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list verification requirements',
    severity: 'high',
    regulationCode: '42 U.S.C. § 1320a-7',
    regulationName: 'Federal Exclusion List Requirements',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicare or SoonerCare (Medicaid) funded services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you check the OIG exclusion list for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly' },
          { value: 'hire_annual', label: 'Yes, at hire and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No / Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'hire_annual', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['hire_only', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification meets federal requirements.',
      atRisk: 'OIG checks should be performed monthly per OIG guidance.',
      nonCompliant: 'OIG screening is inadequate. Civil Monetary Penalties up to $100,000 per occurrence possible.',
      unknown: 'Verify OIG exclusion list procedures at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'Monthly OIG LEIE screening recommended per 42 U.S.C. Section 1320a-7',
      'SAM.gov exclusion check also required for federal program participants',
      'Civil monetary penalties up to $100,000 per excluded individual employed',
      'Document all exclusion screenings in personnel files',
    ],
  },
];

export default OKLAHOMA_QUESTIONS;